import React from 'react';
import { Box, Container, Grid, Paper, Typography, Divider, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Static UI-only preview of Comparison Page (no data fetching, no contexts)
// Mirrors the visual layout of ComparisonPage cards with our updated pricing presentation rules:
// - Neutral white cards with thin gray borders
// - Savings shown under monthly price, no "/year"
// - No yearly green line or overpayment
// - VAT 20% line is shown under "to pay" to mimic UA-only display (for preview purposes)

const PLAN_ORDER = ['360-days', '180-days', '90-days', '360-days-split'];
const PRODUCT_ORDER = ['Call Tracking', 'Business Phone', 'Мовна аналітика з ШІ'];
const numericSx = { fontVariantNumeric: 'tabular-nums' };

const demoOffers = [
  {
    id: 'offer-1',
    name: 'Offer 1',
    monthlyTotal: '$72',
    products: [
      { name: 'Call Tracking', qtyLabel: '20 слотів', price: '$72' },
      { name: 'Business Phone', excluded: true },
      { name: 'Мовна аналітика з ШІ', excluded: true },
    ],
    plans: [
      {
        id: '360-days',
        title: '360 days (30% bonus)',
        toPay: '$866',
        vat20: '$1039',
        monthly: '$72',
        savings: '$250',
        isRecommended: true,
      },
      {
        id: '180-days',
        title: '180 days (15% bonus)',
        toPay: '$489',
        vat20: '$587',
        monthly: '$82',
        savings: '$73',
      },
      {
        id: '90-days',
        title: '90 days',
        toPay: '$281',
        vat20: '$338',
        monthly: '$94',
      },
      {
        id: '360-days-split',
        title: '360 days (split payment)',
        toPay: '$1125',
        vat20: '$1350',
        monthly: '$94',
      },
    ],
  },
  {
    id: 'offer-4',
    name: 'Offer #4',
    monthlyTotal: '$65',
    products: [
      { name: 'Call Tracking', qtyLabel: '15 слотів', price: '$65' },
      { name: 'Business Phone', qtyLabel: '62 користувачів', price: '$566' },
      { name: 'Мовна аналітика з ШІ', qtyLabel: '100 хвилин', price: '$3' },
    ],
    plans: [
      {
        id: '360-days',
        title: '360 days',
        toPay: '$774',
        vat20: '$929',
        monthly: '$65',
        savings: '$0',
        isRecommended: true,
      },
      {
        id: '180-days',
        title: '180 днів (бонус 10%)',
        excluded: true,
      },
      {
        id: '90-days',
        title: '90 днів',
        excluded: true,
      },
      {
        id: '360-days-split',
        title: '360 днів (розстрочка)',
        excluded: true,
      },
    ],
  },
];

export default function ComparisonTestPage() {
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', py: 4 }}>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            {t('offers.offersComparison', 'Offers Comparison (UI Preview)')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('offers.uiPreviewOnly', 'UI preview only — static values, no logic.')}
          </Typography>
        </Box>

        <Grid container spacing={3} justifyContent="center">
          {demoOffers.map((offer) => (
            <Grid item xs={12} md={6} lg={5} key={offer.id}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: '1px solid rgba(0, 0, 0, 0.10)',
                  overflow: 'hidden',
                  bgcolor: '#fff',
                }}
              >
                {/* Header */}
                <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {offer.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Typography variant="h4" sx={{ color: '#00C853', fontWeight: 700 }}>
                      {offer.monthlyTotal}
                    </Typography>
                    <Typography sx={{ color: '#00C853' }}>
                      {t('calculator.perMonthShort', '/mo')}
                    </Typography>
                  </Box>
                </Box>

                {/* Products Section */}
                <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                    {t('offers.productsAndUsage', 'Products and Usage')}
                  </Typography>
                  {offer.products
                    .slice()
                    .sort((a, b) => PRODUCT_ORDER.indexOf(a.name) - PRODUCT_ORDER.indexOf(b.name))
                    .map((p, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 2,
                          minHeight: '80px',
                          borderBottom: '1px solid rgba(0,0,0,0.06)'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: p.excluded ? 'text.secondary' : 'text.primary' }}>
                            {p.name}
                          </Typography>
                          {!p.excluded && (
                            <Typography variant="body2" color="text.secondary">{p.qtyLabel}</Typography>
                          )}
                        </Box>
                        {!p.excluded ? (
                          <Typography variant="body2" sx={{ fontWeight: 600, ...numericSx }}>
                            {p.price}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            {t('offers.notIncluded', 'Не включено')}
                          </Typography>
                        )}
                      </Box>
                    ))}
                </Box>

                {/* Plans Section */}
                <Box sx={{ p: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                    {t('calculator.subscriptionOptions', 'Subscription Options')}
                  </Typography>

                  {offer.plans
                    .slice()
                    .sort((a, b) => PLAN_ORDER.indexOf(a.id) - PLAN_ORDER.indexOf(b.id))
                    .map((plan) => (
                    <Paper
                      key={plan.id}
                      elevation={0}
                      sx={{
                        p: 2,
                        mb: 1.5,
                        bgcolor: '#fff',
                        border: plan.isRecommended ? '2px solid' : '1px solid',
                        borderColor: plan.isRecommended ? 'rgba(0, 200, 83, 0.35)' : 'rgba(0, 0, 0, 0.10)',
                        borderRadius: 1,
                        opacity: plan.excluded ? 0.5 : 1,
                        minHeight: '80px',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {plan.title}
                        </Typography>
                        {!plan.excluded ? (
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, ...numericSx }}>
                              {plan.toPay} {t('subscription.toPay', 'to pay')}
                            </Typography>
                            {/* UA VAT preview */}
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, ...numericSx }}>
                              {t('subscription.vatIncluded', 'З ПДВ 20%: {{amount}}', { amount: plan.vat20 })}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            {t('offers.notIncluded', 'Not included')}
                          </Typography>
                        )}
                      </Box>

                      {!plan.excluded ? (
                        <Box sx={{ mt: 1.5, minHeight: 36 }}>
                          <Typography variant="body2" sx={{ color: '#00C853', fontWeight: 600, ...numericSx }}>
                            {plan.monthly} {t('subscription.perMonth', '/month')}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: '#00C853', display: 'block', mt: 0.25, ...numericSx, visibility: (plan.savings && plan.savings !== '$0') ? 'visible' : 'hidden' }}
                          >
                            {t('subscription.savings', 'Savings')} {plan.savings || '$0'}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ mt: 1.5, minHeight: 36 }}>
                          <Typography variant="body2" sx={{ visibility: 'hidden' }}>
                            placeholder
                          </Typography>
                          <Typography variant="caption" sx={{ visibility: 'hidden' }}>
                            placeholder
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  ))}

                  <Divider sx={{ my: 2 }} />
                  <Button fullWidth variant="contained" color="primary" sx={{ borderRadius: 1, py: 1.25 }}>
                    {t('calculator.viewDetails', 'Детальна пропозиція')}
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
